import apiClient, { handleApiError } from '../../utils/apiClient';
import type { ShiftSlotStatus } from '../../domain/dailyShiftSlot';

/**
 * APIレスポンスの共通型
 *
 * @template T - レスポンスデータの型
 * @property {boolean} success - API呼び出し成功フラグ
 * @property {T} [data] - レスポンスデータ（成功時）
 * @property {object} [error] - エラー情報（失敗時）
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * 月次シフト枠サマリーAPIレスポンス型（camelCase）
 *
 * @description バックエンドAPIからのレスポンス形式
 */
interface MonthlyShiftSlotSummaryApiItem {
  id: string;
  year: number;
  month: number;
  status: ShiftSlotStatus;
  submissionRate: number;
  unconfirmedSlots: number;
}

/**
 * 月次シフト枠サマリー一覧APIレスポンス型
 *
 * @description バックエンドAPIからのレスポンス形式（snake_case）
 *
 * @property {MonthlyShiftSlotSummaryApiItem[]} data - サマリーデータ配列
 * @property {object} meta - ページネーションメタ情報
 */
interface MonthlyShiftSlotSummaryListApiResponse {
  data: MonthlyShiftSlotSummaryApiItem[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

/**
 * 月次シフト枠API クライアント
 *
 * @description
 * 月次シフト枠管理に関するAPIエンドポイントを呼び出す。
 * エラーハンドリングを統一し、日本語エラーメッセージを提供する。
 */
export const monthlyShiftSlotApi = {
  /**
   * 月次シフト枠サマリー一覧を取得する
   *
   * @param {object} [params] - クエリパラメータ
   * @param {number} [params.page] - ページ番号（1始まり）
   * @param {number} [params.page_size] - 1ページあたりの件数
   * @returns {Promise<MonthlyShiftSlotSummaryListApiResponse>} サマリー一覧レスポンス
   * @throws {Error} API呼び出し失敗時
   */
  getSummaryList: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<MonthlyShiftSlotSummaryListApiResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<MonthlyShiftSlotSummaryListApiResponse>>(
        '/api/monthly-shift-slots/summary',
        { params }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '一覧取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 月次シフト枠を削除する
   *
   * @param {string} id - 削除対象の月次シフト枠ID
   * @returns {Promise<void>}
   * @throws {Error} 削除失敗時（確定済み予約がある場合など）
   */
  delete: async (id: string): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/monthly-shift-slots/${id}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '削除に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
};
